import React, { useState } from "react";
import { Download, Loader2, Info } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PAYMENT_MODES = [
  { key: "card", label: "CB (Stripe)" },
  { key: "monthly", label: "SEPA 8x (Stripe)" },
  { key: "cheque", label: "Chèque" },
  { key: "especes", label: "Espèces" },
  { key: "virement", label: "Virement" },
];

const PAYMENT_STATUSES = [
  { key: "paid", label: "Payé" },
  { key: "partial", label: "Partiel" },
  { key: "pending", label: "En attente" },
  { key: "cancelled", label: "Annulé" },
];

const ORDER_TYPES = [
  { key: "inscription", label: "Inscription (site)" },
  { key: "reinscription", label: "Réinscription (portail)" },
  { key: "stage", label: "Stage / camp" },
];

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full transition border ${
      active
        ? "bg-deep-rose text-white border-deep-rose"
        : "bg-white text-stone-700 border-stone-300 hover:border-deep-rose"
    }`}
  >
    {children}
  </button>
);

const AccountingAdmin = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [modes, setModes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const toggle = (arr, setArr, key) => setArr(arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key]);

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (modes.length) params.append("payment_mode", modes.join(","));
      if (statuses.length) params.append("payment_status", statuses.join(","));
      if (types.length) params.append("order_type", types.join(","));
      const res = await api.get(`/admin/accounting/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `elan-danse-comptabilite-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error("Erreur lors de l'export : " + (e?.response?.data?.detail || e.message));
    } finally { setDownloading(false); }
  };

  return (
    <div>
      <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Comptabilité</h1>
      <p className="text-stone-500 mt-1 text-sm sm:text-base">Exporter les commandes du site avec réconciliation Stripe. Filtrez par période, mode de paiement, statut ou type de commande, puis téléchargez en Excel.</p>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mt-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="accounting-date-from" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-stone-500 mb-1.5">Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="accounting-date-to" className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blush" />
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Modes de paiement <span className="text-stone-400 normal-case">(vide = tous)</span></div>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_MODES.map((m) => (
              <Chip key={m.key} active={modes.includes(m.key)} onClick={() => toggle(modes, setModes, m.key)}>{m.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Statuts <span className="text-stone-400 normal-case">(vide = tous)</span></div>
          <div className="flex flex-wrap gap-2">
            {PAYMENT_STATUSES.map((s) => (
              <Chip key={s.key} active={statuses.includes(s.key)} onClick={() => toggle(statuses, setStatuses, s.key)}>{s.label}</Chip>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Types de commande <span className="text-stone-400 normal-case">(vide = tous)</span></div>
          <div className="flex flex-wrap gap-2">
            {ORDER_TYPES.map((t) => (
              <Chip key={t.key} active={types.includes(t.key)} onClick={() => toggle(types, setTypes, t.key)}>{t.label}</Chip>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={download}
            disabled={downloading}
            data-testid="accounting-download-btn"
            className="inline-flex items-center gap-2 bg-coral text-white px-6 py-2.5 rounded-full text-sm font-medium disabled:opacity-50 hover:brightness-105 transition"
          >
            {downloading ? <Loader2 className="animate-spin" size={16}/> : <Download size={16}/>}
            {downloading ? "Génération…" : "Télécharger l'export Excel"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mt-6 border-l-4 border-emerald-400">
        <h3 className="font-serif-display text-lg text-stone-800 mb-1 flex items-center gap-2"><Info size={20} className="text-emerald-700"/> Réconciliation Stripe</h3>
        <p className="text-stone-500 text-sm">
          Chaque commande dispose d&apos;un numéro unique du type <code className="bg-stone-100 px-1.5 py-0.5 rounded text-xs">ELN-2026-00042</code>. Ce numéro est envoyé à Stripe :
        </p>
        <ul className="list-disc list-inside text-stone-600 text-sm mt-2 space-y-1">
          <li>Dans le champ <strong>Client Reference ID</strong> (SEPA) — visible dans le dashboard Stripe.</li>
          <li>Dans les <strong>metadata</strong> de chaque session Checkout, Customer et Subscription.</li>
        </ul>
        <p className="text-stone-500 text-sm mt-3">
          Pour retrouver une commande dans Stripe : dashboard → Recherche globale → collez le numéro ELN-YYYY-NNNNN.
        </p>
      </div>
    </div>
  );
};

export default AccountingAdmin;
